import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_team-expense/artifacts/1f23zen4_Screenshot%202026-01-29%20at%2010.35.14%E2%80%AFPM.png";

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Show splash for 2.5 seconds on every page load
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 2500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
        >
          {/* Gradient background */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.3, scale: 1.2 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-violet-600 to-purple-600 blur-[120px]"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-cyan-500 blur-[100px]"
            />
          </div>

          {/* Logo container */}
          <div className="relative flex flex-col items-center">
            {/* Logo with scale and glow animation */}
            <motion.div
              initial={{ scale: 0, rotate: -180, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ 
                duration: 0.8, 
                ease: [0.34, 1.56, 0.64, 1], // Spring-like bounce
                delay: 0.2 
              }}
              className="relative"
            >
              {/* Glow effect behind logo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: [0, 0.8, 0.4], scale: [0.5, 1.2, 1] }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="absolute inset-0 bg-violet-500 rounded-3xl blur-2xl"
              />
              
              {/* Logo image */}
              <motion.img
                src={LOGO_URL}
                alt="SharePay"
                className="relative w-24 h-24 sm:w-32 sm:h-32 object-contain rounded-3xl"
                initial={{ filter: "brightness(0)" }}
                animate={{ filter: "brightness(1)" }}
                transition={{ duration: 0.5, delay: 0.6 }}
              />
            </motion.div>

            {/* Text with staggered reveal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-8 text-center"
            >
              {/* SharePay text with gradient */}
              <motion.h1 
                className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white via-violet-200 to-violet-400 bg-clip-text text-transparent"
                initial={{ letterSpacing: "0.5em", opacity: 0 }}
                animate={{ letterSpacing: "0.05em", opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
              >
                SharePay
              </motion.h1>
              
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                className="mt-3 text-gray-400 text-sm sm:text-base tracking-wider"
              >
                Money with Friends. Gamified.
              </motion.p>
            </motion.div>

            {/* Loading bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="mt-10 w-48 h-1 bg-white/10 rounded-full overflow-hidden"
            >
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: "0%" }}
                transition={{ duration: 0.8, delay: 1.7, ease: "easeInOut" }}
                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
              />
            </motion.div>
          </div>

          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                opacity: 0,
                x: Math.random() * 400 - 200,
                y: Math.random() * 400 - 200,
                scale: 0
              }}
              animate={{ 
                opacity: [0, 0.6, 0],
                y: [0, -100],
                scale: [0, 1, 0.5]
              }}
              transition={{ 
                duration: 2,
                delay: 0.5 + i * 0.2,
                repeat: 0
              }}
              className="absolute w-2 h-2 rounded-full bg-violet-400"
              style={{
                left: `${30 + i * 10}%`,
                top: `${40 + (i % 3) * 10}%`
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
